import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Pencil,
  Target,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  PageHeader,
  Spinner,
  useToast,
} from '../../ui';
import { useGoal } from '../../features/goals/useGoals';
import {
  formatDate,
  statusLabel,
  statusTone,
} from '../../features/goals/goalModel';
import {
  GOAL_FRAMEWORKS,
  GOAL_LEVELS,
  GOAL_TYPES,
  PLANNING_PERIODS,
  optionLabel,
} from '../../features/goals/goalConfig';
import type { GoalSummary } from '../../features/goals/types';
import { GoalHealthPanel } from './GoalHealthPanel';
import { TraceabilityPanel } from './TraceabilityPanel';
import { GoalKnowledgePanel } from './GoalKnowledgePanel';
import { GoalModal } from './GoalModal';

/** A labeled block of narrative text (hidden when empty). */
function Field({ label, value }: { label: string; value?: string }) {
  if (!value || !value.trim()) return null;
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-zinc-500">{label}</p>
      <p className="mt-0.5 whitespace-pre-wrap text-sm text-zinc-300">{value}</p>
    </div>
  );
}

/** A labeled list of chips (hidden when empty). */
function ChipList({ label, items }: { label: string; items?: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <p className="mb-1.5 text-xs uppercase tracking-wide text-zinc-500">
        {label}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {items.map((i) => (
          <Badge key={i} tone="neutral">
            {i}
          </Badge>
        ))}
      </div>
    </div>
  );
}

/**
 * Goal detail — lightweight by default (title, narrative, status, ownership,
 * milestone, progress) with an expandable "Advanced" section for the remaining
 * fields, alongside the client-side Engineering Traceability rollup and the
 * advisory Goal Health signal (with human override). Edits reuse the GoalModal.
 */
export function GoalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    goal,
    allGoals,
    traceability,
    sprintTaskProgress,
    health,
    loading,
    error,
    save,
    applyStatus,
  } = useGoal(id);

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const byId = useMemo(() => {
    const m = new Map<string, GoalSummary>();
    for (const g of allGoals) m.set(g.id, g);
    return m;
  }, [allGoals]);

  const parent = goal?.parentGoalId ? byId.get(goal.parentGoalId) : undefined;
  const children = useMemo(
    () => (goal ? allGoals.filter((g) => g.parentGoalId === goal.id) : []),
    [allGoals, goal],
  );
  const related = useMemo(
    () =>
      goal
        ? (goal.relatedGoalIds ?? [])
            .map((rid) => byId.get(rid))
            .filter((g): g is GoalSummary => !!g)
        : [],
    [goal, byId],
  );

  const onApplyStatus = async (
    ...args: Parameters<typeof applyStatus>
  ) => {
    const saved = await applyStatus(...args);
    toast({ tone: 'success', title: 'Status updated', description: statusLabel(saved.status) });
    return saved;
  };

  const onSave = async (draft: Parameters<typeof save>[0]) => {
    const saved = await save(draft);
    toast({ tone: 'success', title: 'Goal saved', description: saved.title });
    return saved;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner label="Loading goal" />
      </div>
    );
  }

  if (error || !goal) {
    return (
      <div>
        <PageHeader
          icon={<Target className="h-5 w-5" />}
          title="Goal"
          actions={
            <Button
              variant="ghost"
              leftIcon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => navigate('/goals')}
            >
              Back to Goals
            </Button>
          }
        />
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title="Couldn't load goal"
          description={error ?? 'This goal does not exist or was deleted.'}
        />
      </div>
    );
  }

  const progress = goal.manualProgress;

  return (
    <div>
      <PageHeader
        icon={<Target className="h-5 w-5" />}
        title={goal.title}
        description={
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <Badge tone={statusTone(goal.status)} dot>
              {statusLabel(goal.status)}
            </Badge>
            <span className="text-zinc-600">·</span>
            <span>{optionLabel(GOAL_TYPES, goal.goalType)}</span>
            <span className="text-zinc-600">·</span>
            <span>{optionLabel(GOAL_FRAMEWORKS, goal.goalFramework)}</span>
            <span className="text-zinc-600">·</span>
            <span>{optionLabel(PLANNING_PERIODS, goal.planningPeriod)}</span>
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              leftIcon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => navigate('/goals')}
            >
              Back
            </Button>
            <Button
              leftIcon={<Pencil className="h-4 w-4" />}
              onClick={() => setEditOpen(true)}
            >
              Edit
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main column */}
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardBody className="space-y-4">
              {/* Planning & framework facts — surfaced up-front (not buried
                  in Advanced) so OP1/OP2/BHAG/etc., type, level and period are
                  always visible at a glance. */}
              <div className="grid grid-cols-2 gap-3 rounded-xl border border-surface-border bg-surface-1/50 px-3.5 py-3 sm:grid-cols-4">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-zinc-500">
                    Framework
                  </p>
                  <p className="mt-0.5 text-sm font-medium text-zinc-200">
                    {optionLabel(GOAL_FRAMEWORKS, goal.goalFramework)}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-zinc-500">
                    Type
                  </p>
                  <p className="mt-0.5 text-sm font-medium text-zinc-200">
                    {optionLabel(GOAL_TYPES, goal.goalType)}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-zinc-500">
                    Level
                  </p>
                  <p className="mt-0.5 text-sm font-medium text-zinc-200">
                    {optionLabel(GOAL_LEVELS, goal.level)}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-zinc-500">
                    Period
                  </p>
                  <p className="mt-0.5 text-sm font-medium text-zinc-200">
                    {optionLabel(PLANNING_PERIODS, goal.planningPeriod)}
                  </p>
                </div>
              </div>

              <Field label="Description" value={goal.description} />
              <Field label="Why it matters" value={goal.whyItMatters} />
              <Field label="Expected outcome" value={goal.expectedOutcome} />
              <Field label="Success metrics" value={goal.successMetrics} />
              <Field label="Target" value={goal.target} />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-zinc-500">
                    Owning team
                  </p>
                  <p className="mt-0.5 text-sm text-zinc-300">
                    {goal.owningTeam || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-zinc-500">
                    Goal lead
                  </p>
                  <p className="mt-0.5 text-sm text-zinc-300">
                    {goal.goalLead || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-zinc-500">
                    Leadership sponsor
                  </p>
                  <p className="mt-0.5 text-sm text-zinc-300">
                    {goal.leadershipSponsor || '—'}
                  </p>
                </div>
              </div>

              {progress !== undefined && (
                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <p className="text-xs uppercase tracking-wide text-zinc-500">
                      Manual progress
                    </p>
                    <span className="text-xs tabular-nums text-zinc-400">
                      {progress}%
                    </span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-3">
                    <div
                      className="h-full rounded-full bg-accent"
                      style={{ width: `${progress}%` }}
                      role="progressbar"
                      aria-label="Manual progress"
                      aria-valuenow={progress}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    />
                  </div>
                </div>
              )}

              {goal.nextMilestone && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-zinc-500">
                    Next milestone
                  </p>
                  <p className="mt-0.5 text-sm text-zinc-300">
                    {goal.nextMilestone.label}
                    {goal.nextMilestone.dueDate && (
                      <span className="text-zinc-500">
                        {' '}
                        · due {formatDate(goal.nextMilestone.dueDate)}
                      </span>
                    )}
                  </p>
                </div>
              )}

              {goal.statusNote && (
                <div className="rounded-xl border border-surface-border bg-surface-1/50 px-3 py-2.5">
                  <p className="text-xs uppercase tracking-wide text-zinc-500">
                    Status note
                  </p>
                  <p className="mt-0.5 text-sm text-zinc-300">{goal.statusNote}</p>
                </div>
              )}

              {goal.supportingDocLink && (
                <a
                  href={goal.supportingDocLink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-accent-fg hover:underline"
                >
                  <ExternalLink className="h-4 w-4" />
                  Supporting doc
                </a>
              )}

              {/* Advanced toggle */}
              <button
                type="button"
                onClick={() => setShowAdvanced((s) => !s)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-400 hover:text-zinc-200"
              >
                {showAdvanced ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
                {showAdvanced ? 'Hide advanced' : 'Show advanced'}
              </button>

              {showAdvanced && (
                <div className="space-y-4 border-t border-surface-border pt-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-zinc-500">
                        Hierarchy level
                      </p>
                      <p className="mt-0.5 text-sm text-zinc-300">
                        {optionLabel(GOAL_LEVELS, goal.level)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-zinc-500">
                        Parent goal
                      </p>
                      {parent ? (
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/goals/${encodeURIComponent(parent.id)}`)
                          }
                          className="mt-0.5 text-sm text-accent-fg hover:underline"
                        >
                          {parent.title}
                        </button>
                      ) : (
                        <p className="mt-0.5 text-sm text-zinc-500">Root goal</p>
                      )}
                    </div>
                  </div>

                  <ChipList label="POCs" items={goal.pocs} />
                  <ChipList label="Contributors" items={goal.contributors} />
                  <ChipList label="Dependencies" items={goal.dependencies} />
                  <ChipList label="Risks" items={goal.risks} />
                  <ChipList
                    label="Linked initiative ids"
                    items={goal.linkedInitiativeIds}
                  />

                  {related.length > 0 && (
                    <div>
                      <p className="mb-1.5 text-xs uppercase tracking-wide text-zinc-500">
                        Related goals
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {related.map((r) => (
                          <button
                            key={r.id}
                            type="button"
                            onClick={() =>
                              navigate(`/goals/${encodeURIComponent(r.id)}`)
                            }
                          >
                            <Badge tone="info">{r.title}</Badge>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {children.length > 0 && (
                    <div>
                      <p className="mb-1.5 text-xs uppercase tracking-wide text-zinc-500">
                        Child goals ({children.length})
                      </p>
                      <ul className="divide-y divide-surface-border rounded-xl border border-surface-border">
                        {children.map((c) => (
                          <li key={c.id}>
                            <button
                              type="button"
                              onClick={() =>
                                navigate(`/goals/${encodeURIComponent(c.id)}`)
                              }
                              className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-surface-3"
                            >
                              <span className="min-w-0 truncate text-sm text-zinc-200">
                                {c.title}
                              </span>
                              <Badge tone={statusTone(c.status)}>
                                {statusLabel(c.status)}
                              </Badge>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </CardBody>
          </Card>

          {traceability && <TraceabilityPanel traceability={traceability} />}

          {sprintTaskProgress && sprintTaskProgress.taskCount > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>
                  <span className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-accent" aria-hidden="true" />
                    Sprint work
                  </span>
                </CardTitle>
                <span className="text-xs text-zinc-500">
                  {sprintTaskProgress.doneCount}/{sprintTaskProgress.taskCount} tasks
                </span>
              </CardHeader>
              <CardBody className="space-y-3">
                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <p className="text-xs uppercase tracking-wide text-zinc-500">
                      Completion (by effort)
                    </p>
                    <span className="text-xs tabular-nums text-zinc-400">
                      {sprintTaskProgress.percentComplete}%
                    </span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-3">
                    <div
                      className="h-full rounded-full bg-accent"
                      style={{ width: `${sprintTaskProgress.percentComplete}%` }}
                      role="progressbar"
                      aria-label="Completion by effort"
                      aria-valuenow={sprintTaskProgress.percentComplete}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-zinc-500">
                      Completed effort
                    </p>
                    <p className="mt-0.5 tabular-nums text-emerald-300">
                      {sprintTaskProgress.completedDays}d
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-zinc-500">
                      Committed effort
                    </p>
                    <p className="mt-0.5 tabular-nums text-zinc-200">
                      {sprintTaskProgress.committedDays}d
                    </p>
                  </div>
                </div>
                <p className="text-xs text-zinc-600">
                  Aggregated from tasks linked to this goal — effort and completion
                  of its sprint work.
                </p>
              </CardBody>
            </Card>
          )}

          <GoalKnowledgePanel goal={goal} />
        </div>

        {/* Sidebar column */}
        <div className="space-y-6">
          {health && (
            <GoalHealthPanel
              health={health}
              currentStatus={goal.status}
              currentNote={goal.statusNote}
              onApply={onApplyStatus}
            />
          )}
          <Card>
            <CardHeader>
              <CardTitle>Metadata</CardTitle>
            </CardHeader>
            <CardBody className="space-y-2 text-sm text-zinc-400">
              <p>
                Created by{' '}
                <span className="text-zinc-200">{goal.createdBy || '—'}</span>
              </p>
              <p>Last updated {formatDate(goal.lastUpdated) || '—'}</p>
            </CardBody>
          </Card>
        </div>
      </div>

      <GoalModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        goal={goal}
        goals={allGoals}
        onCreate={onSave}
        onSave={onSave}
      />
    </div>
  );
}

export default GoalDetailPage;
